/**
 * Aba "Categorias" de /financeiro/cadastros · redesign FIN-7.
 * Chips agrupados por classificação DRE (fiel ao mockup); clique no chip abre
 * edição. CRUD e endpoints inalterados.
 */
import React, { useCallback, useEffect, useState } from "react";
import { FiPlus } from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import SlideOverPanel from "../../../components/SlideOverPanel";
import { useAuth } from "../../../context/AuthContext";
import {
  atualizarCategoria, CLASSIFICACOES_DRE, criarCategoria,
  desativarCategoria, listarCategorias, TIPOS_CATEGORIA,
} from "../../../services/financeiro";
import { Input, SecondaryButton, Select, Textarea } from "../../Orcamentos/styles";


// Ordem oficial DRE + rótulos curtos para os grupos de chips.
const ORDEM_CLAS = [
  ["RECEITA_OPERACIONAL", "1 · Receita operacional"],
  ["DEDUCAO_RECEITA_BRUTA", "2 · Dedução da receita"],
  ["CUSTO_OPERACIONAL", "3 · Custo operacional"],
  ["DESPESA_OPERACIONAL", "4 · Despesa operacional"],
  ["RECEITA_FINANCEIRA", "5 · Receita financeira"],
  ["DESPESA_FINANCEIRA", "6 · Despesa financeira"],
  ["RECEITA_NAO_OPERACIONAL", "7 · Receita não operacional"],
  ["DESPESA_NAO_OPERACIONAL", "8 · Despesa não operacional"],
  ["INVESTIMENTO_EMPRESTIMO", "9 · Investimento / empréstimo"],
  ["OUTROS", "10 · Outros / não classificadas"],
];


function CategoriaForm({ inicial, onSave, onCancel, onDelete, categorias, unidadeId }) {
  const [dados, setDados] = useState({
    nome: inicial?.nome || "",
    tipo: inicial?.tipo || "RECEITA",
    classificacao_dre: inicial?.classificacao_dre || "OUTROS",
    categoria_pai: inicial?.categoria_pai || "",
    descricao: inicial?.descricao || "",
  });
  const possiveisPais = categorias.filter((c) =>
    c.tipo === dados.tipo && c.id !== inicial?.id && !c.categoria_pai);
  const classificacoesValidas = CLASSIFICACOES_DRE.filter((c) =>
    c.tipo_aplicavel === dados.tipo || c.tipo_aplicavel === "AMBOS");

  const handleTipoChange = (novoTipo) => {
    const classOk = CLASSIFICACOES_DRE.find((c) =>
      c.value === dados.classificacao_dre &&
      (c.tipo_aplicavel === novoTipo || c.tipo_aplicavel === "AMBOS"));
    setDados({ ...dados, tipo: novoTipo, categoria_pai: "", classificacao_dre: classOk ? dados.classificacao_dre : "OUTROS" });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>Nome *</span>
        <Input value={dados.nome} onChange={(e) => setDados({ ...dados, nome: e.target.value })} maxLength={120} />
      </label>
      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>Tipo *</span>
        <Select value={dados.tipo} onChange={(e) => handleTipoChange(e.target.value)}>
          {TIPOS_CATEGORIA.map((t) => (<option key={t.value} value={t.value}>{t.label}</option>))}
        </Select>
      </label>
      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>Classificação DRE *</span>
        <Select value={dados.classificacao_dre} onChange={(e) => setDados({ ...dados, classificacao_dre: e.target.value })}>
          {classificacoesValidas.map((c) => (<option key={c.value} value={c.value}>{c.label}</option>))}
        </Select>
        <span style={{ fontSize: 11, opacity: 0.6 }}>
          Define onde essa categoria aparece na DRE oficial. “Outros” = não entra em nenhum bloco padrão.
        </span>
      </label>
      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>Categoria pai (opcional)</span>
        <Select value={dados.categoria_pai} onChange={(e) => setDados({ ...dados, categoria_pai: e.target.value })}>
          <option value="">Nenhuma (categoria raiz)</option>
          {possiveisPais.map((c) => (<option key={c.id} value={c.id}>{c.nome}</option>))}
        </Select>
      </label>
      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>Descrição</span>
        <Textarea rows={2} value={dados.descricao} onChange={(e) => setDados({ ...dados, descricao: e.target.value })} />
      </label>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", alignItems: "center" }}>
        {onDelete && <AsyncButton variant="danger" silentSuccess onClick={onDelete} style={{ marginRight: "auto" }}>Desativar</AsyncButton>}
        <SecondaryButton onClick={onCancel}>Cancelar</SecondaryButton>
        <AsyncButton variant="primary"
                     onClick={() => onSave({ ...dados, categoria_pai: dados.categoria_pai || null, unidade: unidadeId })}
                     successMessage="Categoria salva">Salvar</AsyncButton>
      </div>
    </div>
  );
}


export default function CategoriasTab() {
  const { request, hasAllPermissions, isMaster } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editando, setEditando] = useState(null);
  const [unidadeId, setUnidadeId] = useState("");

  const podeManage = isMaster || hasAllPermissions(["financeiro:configurar"]);

  const carregar = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const p = await listarCategorias(request);
      const list = Array.isArray(p) ? p : p?.results || [];
      setItems(list);
      if (list.length > 0 && !unidadeId) setUnidadeId(list[0].unidade);
    } catch (err) {
      setError(err?.message || "Erro.");
    } finally { setLoading(false); }
  }, [request, unidadeId]);

  useEffect(() => { carregar(); }, [carregar]);

  useEffect(() => {
    if (unidadeId) return;
    request("/api/unidades").then((d) => {
      const list = Array.isArray(d) ? d : d?.results || [];
      if (list.length > 0) setUnidadeId(list[0].id);
    }).catch(() => {});
  }, [request, unidadeId]);

  const handleSalvar = async (dados) => {
    if (editando === "novo") await criarCategoria(request, dados);
    else await atualizarCategoria(request, editando.id, dados);
    setEditando(null);
    await carregar();
  };

  const handleExcluir = async (c) => {
    if (!window.confirm(`Desativar categoria "${c.nome}"?`)) return;
    await desativarCategoria(request, c.id);
    setEditando(null);
    await carregar();
  };

  const grupos = ORDEM_CLAS
    .map(([clas, label]) => [clas, label, items.filter((c) => (c.classificacao_dre || "OUTROS") === clas)])
    .filter(([, , lista]) => lista.length > 0);

  return (
    <>
      <div className="flex between" style={{ marginBottom: 12, alignItems: "center" }}>
        <span className="tiny mono muted">{loading ? "Carregando…" : `${items.length} categoria(s)`}</span>
        {podeManage && (
          <button type="button" className="btn btn--primary btn--sm" onClick={() => setEditando("novo")}>
            <FiPlus size={14} aria-hidden="true" /> Nova categoria
          </button>
        )}
      </div>
      {error && <div className="empty"><div className="msg">{error}</div></div>}

      <div className="table-wrap">
        {grupos.length === 0 ? (
          <div className="empty"><div className="msg">{loading ? "Carregando…" : "Nenhuma categoria."}</div></div>
        ) : grupos.map(([clas, label, lista]) => (
          <div key={clas} className="cat-group">
            <div className="cat-group__head">
              <span className="badge no-dot" style={{ background: "var(--accent-soft)", color: "var(--accent)" }}>{label}</span>
              <span className="tiny mono muted">{lista.length} categoria{lista.length > 1 ? "s" : ""}</span>
            </div>
            <div className="cat-rows">
              {lista.map((c) => (
                <button key={c.id} type="button" className="cat-chip"
                        style={{ cursor: podeManage ? "pointer" : "default", opacity: c.ativo ? 1 : 0.5 }}
                        onClick={() => podeManage && setEditando(c)}
                        title={podeManage ? "Editar" : c.nome}>
                  {c.nome}{!c.ativo ? " (inativa)" : ""}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {editando && (
        <SlideOverPanel open onClose={() => setEditando(null)}
                        title={editando === "novo" ? "Nova categoria" : `Editar ${editando.nome}`}>
          <CategoriaForm inicial={editando === "novo" ? null : editando} categorias={items}
                         onSave={handleSalvar} onCancel={() => setEditando(null)}
                         onDelete={editando !== "novo" && editando.ativo ? () => handleExcluir(editando) : undefined}
                         unidadeId={unidadeId} />
        </SlideOverPanel>
      )}
    </>
  );
}
